import { usePersistedState } from "@/lib/persistedFilters";
// UC CDR → Partner reports. Email a partner (or a single domain) only their own
// usage for the window chosen at the top of the page — summary in the body, the
// legs attached as the same WebCDR-shaped CSV the statements export.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Eye, Loader2, Mail, Search, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pill, clock, money } from "./ui";

export default function Reports({ window: win }) {
  const { has } = useAuth();
  const [scope, setScope] = useState("territory");
  const [options, setOptions] = useState({ territories: [], domains: [] });
  const [q, setQ] = usePersistedState("uccdr-reports.q", "");
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [attach, setAttach] = useState(true);
  const [busy, setBusy] = useState("");
  const [sends, setSends] = useState([]);

  const params = { start: win?.from_at || "", end: win?.to_at || "", tz: win?.tz || "UTC" };

  const load = useCallback(async () => {
    if (!win?.from_at) return;
    const [{ data }, { data: s }] = await Promise.all([
      api.get("/uc-cdr/reports/scopes", { params: { start: win.from_at, end: win.to_at, tz: win.tz } }),
      api.get("/uc-cdr/reports/sent", { params: { limit: 10 } }),
    ]);
    setOptions({ territories: data.territories || [], domains: data.domains || [] });
    setSends(s.sends || []);
  }, [win?.from_at, win?.to_at, win?.tz]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPicked(null); setPreview(null); }, [win?.from_at, win?.to_at]);

  const rows = (scope === "territory" ? options.territories : options.domains)
    .filter((r) => !q || `${r.label} ${r.value}`.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 60);

  const choose = async (row) => {
    setPicked(row);
    setBusy("preview");
    try {
      const { data } = await api.get("/uc-cdr/reports/preview",
                                     { params: { ...params, scope, value: row.value } });
      setPreview(data);
      setRecipients((data.suggested_recipients || []).join(", "));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build that preview");
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    const list = recipients.split(/[,;\s]+/).filter((r) => r.includes("@"));
    if (!list.length) return toast.error("Add at least one recipient email");
    setBusy("send");
    try {
      const { data } = await api.post("/uc-cdr/reports/send", {
        scope, value: picked.value, recipients: list, note, attach, ...params,
      });
      toast.success(data.status === "paused"
        ? "Outbound email is PAUSED platform-wide — the report was not delivered"
        : `Report sent to ${list.join(", ")}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that report");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="uc-cdr-reports">
      <div className="flex flex-wrap items-center gap-2">
        {[["territory", "By partner"], ["domain", "By domain"]].map(([value, label]) => (
          <Button key={value} size="sm" variant={scope === value ? "default" : "outline"}
                  data-testid={`uc-cdr-report-scope-${value}`}
                  onClick={() => { setScope(value); setPicked(null); setPreview(null); }}>
            {label}
          </Button>
        ))}
        <div className="relative ml-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-64 pl-8" placeholder="Partner or domain" value={q}
                 data-testid="uc-cdr-report-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <span className="text-xs text-slate-500" data-testid="uc-cdr-report-window">
          {win?.label || "pick a range above"}
        </span>
        {has("reports.view") && (
          <Button size="sm" variant="outline" className="ml-auto" asChild data-testid="uc-cdr-report-hub-link">
            <Link to="/reports/uc_cdr_usage" title="Put this usage report on a monthly schedule (Reports hub)">
              <CalendarClock className="h-4 w-4" /> Schedule monthly
            </Link>
          </Button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700">
            {scope === "territory" ? "Partners with traffic" : "Domains with traffic"}
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <table className="w-full text-left text-sm" data-testid="uc-cdr-report-list">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.value}
                      className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${picked?.value === r.value ? "bg-sky-50" : ""}`}
                      onClick={() => choose(r)} data-testid={`uc-cdr-report-pick-${r.value}`}>
                    <td className="px-4 py-1.5">
                      <div className="font-medium text-slate-800">{r.label}</div>
                      {scope === "territory" && (
                        <div className="text-[11px] text-slate-400">
                          {r.domains} domains{r.org_email ? ` · ${r.org_email}` : " · no contact on file"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-right text-slate-600">
                      {r.minutes.toLocaleString()} min
                      <div className="text-[11px] text-slate-400">{money(r.amount, r.currency)}</div>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td className="px-4 py-8 text-center text-sm text-slate-500"
                          data-testid="uc-cdr-report-empty">
                    No traffic in this window.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="px-4 py-4" data-testid="uc-cdr-report-compose">
          {!picked ? (
            <div className="py-10 text-center text-sm text-slate-500">
              Pick a partner or a domain on the left to build their report.
            </div>
          ) : busy === "preview" ? (
            <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Building the numbers…
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-base font-semibold text-slate-800">{preview?.label}</div>
                <div className="text-[12px] text-slate-500">{preview?.window_label}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(preview?.totals || []).map((t) => (
                  <Pill key={t.currency} tone="bg-emerald-100 text-emerald-800">
                    {money(t.amount, t.currency)} · {t.minutes.toLocaleString()} min · {t.legs.toLocaleString()} legs
                  </Pill>
                ))}
                <Pill>{(preview?.domains || []).length} domains</Pill>
              </div>

              <div className="max-h-44 overflow-y-auto rounded border border-slate-100">
                <table className="w-full text-left text-[13px]" data-testid="uc-cdr-report-preview">
                  <tbody>
                    {(preview?.domains || []).slice(0, 40).map((d) => (
                      <tr key={d.domain} className="border-t border-slate-100">
                        <td className="px-3 py-1 text-slate-700">{d.domain}</td>
                        <td className="px-3 py-1 text-slate-500">{d.legs.toLocaleString()} legs</td>
                        <td className="px-3 py-1 text-slate-500">{d.minutes.toLocaleString()} min</td>
                        <td className="px-3 py-1 text-slate-800">{money(d.amount, d.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Recipients (comma separated)</Label>
                <Input value={recipients} data-testid="uc-cdr-report-recipients"
                       placeholder="billing@partner.ca"
                       onChange={(e) => setRecipients(e.target.value)} />
                {!preview?.suggested_recipients?.length && (
                  <p className="mt-1 text-[11px] text-amber-700">
                    No contact email on this partner's org record — type one in.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-500">Note at the top of the email (optional)</Label>
                <Textarea rows={2} value={note} data-testid="uc-cdr-report-note"
                          placeholder="Here is your usage for last month."
                          onChange={(e) => setNote(e.target.value)} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" className="h-4 w-4" checked={attach}
                       data-testid="uc-cdr-report-attach"
                       onChange={(e) => setAttach(e.target.checked)} />
                Attach the detailed call records as CSV
              </label>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={send} disabled={!!busy} data-testid="uc-cdr-report-send">
                  {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send report
                </Button>
                <Button size="sm" variant="ghost" onClick={() => choose(picked)} disabled={!!busy}
                        data-testid="uc-cdr-report-refresh">
                  <Eye className="h-4 w-4" /> Refresh numbers
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700">
          Recently sent <span className="font-normal text-slate-400">· times in UTC</span>
        </div>
        {!sends.length ? (
          <div className="px-4 py-6 text-sm text-slate-500" data-testid="uc-cdr-report-sends-empty">
            Nothing sent yet.
          </div>
        ) : (
          <table className="w-full text-left text-sm" data-testid="uc-cdr-report-sends">
            <tbody>
              {sends.map((s) => (
                <tr key={s.send_id} className="border-t border-slate-100">
                  <td className="px-4 py-1.5 font-mono text-[12px] text-slate-600">{clock(s.sent_at)}</td>
                  <td className="px-4 py-1.5 text-slate-800">{s.label}</td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-500">{s.window_label}</td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-500">
                    <Mail className="mr-1 inline h-3 w-3" />{(s.recipients || []).join(", ")}
                  </td>
                  <td className="px-4 py-1.5">
                    <Pill tone={s.status === "sent" ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"}>{s.status}</Pill>
                  </td>
                  <td className="px-4 py-1.5 text-[11px] text-slate-400">{s.sent_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
